/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { ActionTemplate } from '@src/app/model/ActionTemplate';
import { Field } from '@src/app/shared/field/Field';
import { RequestTemplate } from '@src/app/model/RequestTemplate';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { maxValue, minValue, required } from '@src/app/shared/validators';
import { RequestCategoriesService } from '@src/app/modules/crm/request-categories.service';
import { RequestTemplatesService } from '@src/app/modules/crm/request-templates.service';
import { SrTopic } from '@src/app/model/sr-topic';
import { SrClass } from '@src/app/model/sr-class';
import { SrTopicService } from '@src/app/modules/crm/sr-topic.service';
import { SrClassService } from '@src/app/modules/crm/sr-class.service';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { OmcRole } from '@src/app/model/OmcRole';
import { Frequency } from '@src/app/model/frequency';
import { AddRecurringJobsAction } from '@src/app/modules/crm/recurring-job/add-recurring-jobs-action';
import { ScheduleJobsAction } from '@src/app/modules/crm/recurring-job/schedule-jobs-action';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { RecurringJobPageNavRoute } from '@src/app/routes/recurring-job-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { RequestTemplatePageNavRoute } from '@src/app/routes/request-template-page-nav';

@Component({
    selector: 'app-request-template',
    templateUrl: './request-template.component.html',
    styleUrls: ['./request-template.component.scss'],
    imports: [FormPageComponent]
})
export class RequestTemplateComponent extends AbstractPageComponent {
    private dataSvc = inject(RequestTemplatesService);
    private catSvc = inject(RequestCategoriesService);
    private topicSvc = inject(SrTopicService);
    private classSvc = inject(SrClassService);

    private srClasses: SrClass[] = [];
    private topicClass = ServiceRequest.makeTopicAndClassFields(() => this.srClasses);

    jobsLink = FormNavLinkComponent.make('Jobs',
        RecurringJobPageNavRoute, o => ({ templateId: o.id }),
        { calculateValue: o => o['recurring_jobs_count'], formColumn: 4 }
    );

    requestsLink = FormNavLinkComponent.make('Open Requests',
        ServiceRequestPageNavRoute, o => ({ templateId: o.id, srStatus: ServiceRequest.srOpen.id }),
        { value: 'open_requests_count', formColumn: 4 }
    );

    config = new FormConfig({
        navRoute: RequestTemplatePageNavRoute,
        docPath: 'omc-professional/requests/request-templates',
        title: $localize`Standard Requests`,
        help: $localize`Standard requests you raise again and again, ready to be created on demand or scheduled as recurring jobs`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FormTextComponent.make('Name', 'name', { validators: [required], cellOpts: { width: '25%' } }),
                FormTextAreaComponent.make('Description', 'description', { cellOpts: { width: '50%' } }),
                FormPicklistComponent.make('Category', 'categoryId', 'category', { service: this.catSvc }, { formColumn: 2 }),
                ServiceRequest.getPriorityField().override({ formColumn: 2 }),
                this.topicClass.topicField.override({ label: $localize`Default Topic`, formColumn: 2 }),
                this.topicClass.classField.override({ label: $localize`Default Class`, formColumn: 2 }),
                FormPicklistComponent.make('Frequency', 'frequencyId', null,
                    { items: Frequency.ALL_OPTIONS, allowSelectNone: true }, { formColumn: 2 }
                ),
                FormPicklistComponent.make('Default Request Owner', 'ownerOmcRole', null,
                    { items: OmcRole.values }, { formColumn: 3 }
                ),
                FormPicklistComponent.make('Default Follower', 'followerOmcRole', null,
                    { items: OmcRole.values, allowSelectNone: true }, { formColumn: 3, hint: $localize`Will receive notification when closed` }
                ),
                FormPicklistComponent.make('Follow On', 'followOnId', null, { service: this.dataSvc }, { 
                    formColumn: 4,
                    hint: 'A follow on request that user should be prompted to create after closing a request based on the current standard request'
                }),
                FormNumberComponent.make('Follow On After', 'followOnAfter').override({
                    validators: [minValue(0), maxValue(1095)], formColumn: 4,
                    hint: 'Days after closing follow on should be planned for, e.g. 365 days for a year, 1095 for 3 years'
                }),
                this.jobsLink,
                this.requestsLink,                
                new GridField({
                    field: { value: 'actions', cellOpts: { heading: 'Actions' }, visible: Field.formOnly, formRow: 2 },
                    rowFactory: () => [
                        FieldMaker.id({ visible: Field.noShow }),
                        FieldMaker.nameControl({ validators: [required], cellOpts: { heading: 'Name', width: '20%' } }),
                        FormTextComponent.make('Description', 'description', {}),
                        FormNumberComponent.make('Days Offset', 'offset', {}, {
                            validators: [maxValue(30), minValue(-63)],
                            hint: 'Number of days before or after target date, enter -1 for before'
                        }),
                        FormPicklistComponent.make('Default Action Owner', 'ownerOmcRole', null, { items: OmcRole.values }),
                        FormDurationComponent.make('Default Time', 'timeSpent', { validators: [minValue(0), maxValue(960)] }),
                        FieldMaker.rev(),
                        FieldMaker.deleteGridRow(),
                    ],
                    objFactory: () => of(new ActionTemplate()),
                    newOptionText: $localize`Add Action Item`,
                })
            ],
            formLayout: LAYOUT_OPTIONS.fourColOver1,
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: () => of(new RequestTemplate()),
        actions: [new ScheduleJobsAction(), new AddRecurringJobsAction('template')],
        beforeEdit: this.beforeEdit.bind(this),
    });

    constructor() {
        super();
        this.subscriptions.push(
            forkJoin({
                topics: this.topicSvc.get<SrTopic>(false),
                classes: this.classSvc.get<SrClass>(false),
            }).subscribe(r => {
                this.srClasses = r.classes;
                this.topicClass.topicField.setPicklistItems(r.topics);
            })
        );
    }

    beforeEdit(template: RequestTemplate): RequestTemplate {
        this.topicClass.narrowClasses(template.srTopic);
        return template;
    }
}
